import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field, change, touch } from 'redux-form';
import {
  getAvailableDocumentsByNationality,
  getDoc2BrandMap,
  getDocumentTypeAliases,
  getIgnoredDocuments,
} from '../utils/documentMaps';
import { selectRenderer } from '../utils/formRenderers';
import {
  NO_DOCUMENT_RESTRICTION,
  normalizeAllowedDocumentTypes,
} from '../utils/minorDocumentTypes';

/**
 * DocumentTypeField component.
 * Render a document type selector field that automatically updates based on passenger nationality.
 * Filters available document types according to nationality restrictions and brand configuration.
 *
 * @param {object} props - name, dataValueRef, target, passengerNationality, passengerDocumentType, documentIdValue, index, isDisabled, allowedDocumentTypes.
 * @param {string} props.name - The field name for the form.
 * @param {string} props.dataValueRef - Field name of the document ID input to mark as touched for validation.
 * @param {string} props.target - The target context (e.g., 'passengers', 'purchaser').
 * @param {string} props.passengerNationality - The passenger's nationality to filter available documents.
 * @param {string} props.passengerDocumentType - The currently selected document type.
 * @param {string} props.documentIdValue - Current value of the document ID field referenced by dataValueRef; used to skip touching empty fields on programmatic document-type changes.
 * @param {number} props.index - The passenger index in the form array.
 * @param {boolean} props.isDisabled - Whether the field should be disabled.
 * @param {Array<string>} props.allowedDocumentTypes - Document types a minor may hold; empty, absent or non-array means no restriction.
 */
const DocumentTypeField = (props) => {
  const dispatch = useDispatch();
  const {
    name,
    target,
    passengerNationality,
    passengerDocumentType,
    documentIdValue,
    index,
    isDisabled,
    dataValueRef,
    allowedDocumentTypes: allowedDocumentTypesProp,
  } = props;

  const { t } = useTranslation('seats');
  const ignoredDocuments = useMemo(() => getIgnoredDocuments(), []);
  const doc2BrandMap = useMemo(() => getDoc2BrandMap(target), [target]);
  const availableDocumentsByNationality = useMemo(() => getAvailableDocumentsByNationality(), []);
  // Documents the tenant hides on the first passenger's slot (and on the purchaser's). This filter
  // used to run last, on `options` alone, while the emptiness fallback and the substituted
  // `documentType` below were both computed from the wider catalog. Two ways that ends with a select
  // the buyer cannot use: the fallback calls a restricted catalog non-empty even when every entry in
  // it is hidden here, and the substitution can store a document `options` then drops. Fold it in at
  // the source, so "available" already means "selectable on screen".
  const items = useMemo(() => {
    const entries = Object.entries(doc2BrandMap);
    const hidesIgnoredDocuments = name === 'passengers[0].documentType' || name === 'documentType';

    return hidesIgnoredDocuments
      ? entries.filter(([key]) => !ignoredDocuments.includes(key))
      : entries;
  }, [doc2BrandMap, ignoredDocuments, name]);

  // Normalised here, not only at the call sites, so no consumer has to remember. A remote
  // whitelabel row can serve `null` — React `defaultProps` only cover `undefined`, so `null` used to
  // reach `.length` below and blank the whole passengers step.
  //
  // The SAME normaliser the call sites use, per-entry check included: `Array.isArray` alone let
  // `['TI', 3]` through as a restriction, narrowing the picker on the strength of a malformed row.
  // A guard whose whole point is not to trust the caller has to be as strict as the caller's own.
  const allowedDocumentTypes = normalizeAllowedDocumentTypes(allowedDocumentTypesProp);

  // The allowlist arrives as the backend wrote the config row, and the backend validates it against
  // the `document_type` of the request — a PROCESSOR value, translated on the way out
  // (`actions/purchase.js:1505`, `:1646`, `actions/quickPurchase.js:39`). This select works in BRAND
  // values (`getDoc2BrandMap`). Comparing the raw row against brands reads one config row in two
  // vocabularies: it only agrees while brand === processor, which is true for rochoa and need not be
  // for the next tenant. Where they differ the failure is not symmetric — a full mismatch falls open
  // below and merely fails to narrow, but a PARTIAL overlap narrows on the wrong criterion and
  // `blockedByAllowlist` then substitutes the type and clears the number the buyer already typed.
  //
  // Expanding each entry to its aliases accepts the row written in either vocabulary, the same
  // defence `validDocumentTypeForInsurance` already applies — its JSDoc states outright that values
  // "can come from forms as brand values or from saved passengers as processor values".
  const allowedDocumentTypeAliases = useMemo(
    () =>
      allowedDocumentTypes.length === 0
        ? NO_DOCUMENT_RESTRICTION
        : Array.from(new Set(allowedDocumentTypes.flatMap(getDocumentTypeAliases))),
    [allowedDocumentTypes],
  );

  const availableDocuments = useMemo(() => {
    return availableDocumentsByNationality[passengerNationality] || [];
  }, [availableDocumentsByNationality, passengerNationality]);

  // Two filters, applied in one pass but kept distinguishable, because they must have DIFFERENT
  // consequences: a nationality filter may legitimately re-select for the buyer, while a document a
  // minor is not allowed to hold must never be silently relabelled — CC, TI and CE share one number
  // format, so a typed cédula would sail through validation wearing a TI label. `allowlistApplied`
  // is what the substitution effect below reads to tell the two apart.
  const { visibleItems, allowlistApplied } = useMemo(() => {
    const byNationality = (entries) =>
      availableDocuments.length > 0
        ? entries.filter(([, value]) => availableDocuments.includes(value))
        : entries;

    const unrestricted = byNationality(items);

    if (allowedDocumentTypes.length === 0) {
      return { visibleItems: unrestricted, allowlistApplied: false };
    }

    const restricted = byNationality(
      items.filter(([, value]) => allowedDocumentTypeAliases.includes(value)),
    );

    // An empty result means this catalog offers no type a minor may hold: a foreign minor whose
    // nationality only allows PP/CE while the allowlist is TI/RC, or the `passengersSecond` catalog,
    // which is an independent map (NIT / passport-shaped documents) and need not contain TI/RC at
    // all. Leaving the select with no options strands the buyer with no explanation, and on
    // `documentTypeSecond` — which is `required` — makes the form unsubmittable for every minor.
    // Fall back to the unrestricted catalog and let the backend refuse: the picker is a convenience,
    // never the gate. Reporting `allowlistApplied: false` also stops the number-clearing below from
    // firing on a substitution the allowlist did not actually cause.
    return restricted.length > 0
      ? { visibleItems: restricted, allowlistApplied: true }
      : { visibleItems: unrestricted, allowlistApplied: false };
  }, [items, allowedDocumentTypes, allowedDocumentTypeAliases, availableDocuments]);

  // The passenger's own document was dropped by the minor allowlist specifically — not by the
  // nationality filter, and not by an allowlist this catalog could not honour.
  const blockedByAllowlist =
    allowlistApplied &&
    Boolean(passengerDocumentType) &&
    !allowedDocumentTypeAliases.includes(passengerDocumentType);

  // Get the document type from the passenger form if it exists in the available documents
  // otherwise, get the first available document type
  const documentType = useMemo(() => {
    return visibleItems.some(([, value]) => value === passengerDocumentType)
      ? passengerDocumentType
      : visibleItems[0]?.[1] || '';
  }, [visibleItems, passengerDocumentType]);

  const prevDocumentTypeRef = useRef(documentType);

  /**
   * Set the document type in the passenger form and touch the document id
   * field for validation. The touch is gated on the document id already
   * having a value so programmatic document-type changes on initial mount
   * (e.g., the FREE_FARE_DEFAULT_CPF auto-select for a non-autofilled
   * passenger) do not surface a false "required" error on an empty field
   * the user has not interacted with yet.
   */
  useEffect(() => {
    if (index === undefined) return;
    // An allowlist substitution on a disabled select (exchanges, the loyalty holder) has no good
    // outcome: relabelling is how a minor's cédula used to reach the purchase as a Tarjeta de
    // Identidad, and clearing the number strands the buyer on an input they cannot re-open. Leave
    // the value alone and let the backend refuse it with its own message. The nationality
    // substitution below is a display reconciliation with neither hazard, so it still runs for
    // disabled fields, as it did before the allowlist existed.
    if (isDisabled && blockedByAllowlist) return;

    const documentTypeChanged = prevDocumentTypeRef.current !== documentType;
    // `blockedByAllowlist` also fires on the prefill path, where the ref was seeded with the
    // already-substituted value on first render and so nothing would otherwise be dispatched —
    // leaving the store holding the disallowed document while the select displays another.
    if (!documentTypeChanged && !blockedByAllowlist) return;

    dispatch(change('passengers', name, documentType));

    if (blockedByAllowlist) {
      // The number belonged to the document we just replaced. Clear it instead of carrying it
      // over: this is precisely how a minor's cédula used to reach the purchase labelled as a
      // Tarjeta de Identidad.
      dispatch(change('passengers', dataValueRef, ''));
      // And touch it, so the clear is not invisible. Untouched, the buyer sees a number they typed
      // disappear with no explanation and only finds out at submit; touched, the field's own
      // `required` error fires at once and says the number has to be captured again.
      dispatch(touch('passengers', dataValueRef));
    } else if (documentIdValue) {
      dispatch(touch('passengers', dataValueRef));
    }

    prevDocumentTypeRef.current = documentType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType, blockedByAllowlist, isDisabled]);

  const options = useMemo(() => {
    return visibleItems.map(([key, value]) => ({
      label: t('identification', { context: key }),
      value,
    }));
  }, [visibleItems, t]);

  return (
    <Field
      label={t('document_type')}
      placeholder={t('document_type')}
      component={selectRenderer}
      options={options}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      isDisabled={isDisabled}
    />
  );
};

DocumentTypeField.propTypes = {
  name: PropTypes.string.isRequired,
  dataValueRef: PropTypes.string.isRequired,
  target: PropTypes.string,
  passengerNationality: PropTypes.string,
  passengerDocumentType: PropTypes.string,
  documentIdValue: PropTypes.string,
  index: PropTypes.number,
  isDisabled: PropTypes.bool,
  allowedDocumentTypes: PropTypes.arrayOf(PropTypes.string),
};

DocumentTypeField.defaultProps = {
  documentIdValue: '',
  allowedDocumentTypes: NO_DOCUMENT_RESTRICTION,
};

export default DocumentTypeField;
